import React, { useEffect, useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  IconButton, 
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CodeIcon from '@mui/icons-material/Code';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import apiService from '../services/api';

interface ICDCode {
  id: string;
  icdCode: string;
  shortDescription: string;
  longDescription: string;
  codeType: string;
  category: string;
  active: boolean;
  effectiveDate?: string;
  expirationDate?: string;
  billable: boolean;
  snomedMapping?: string;
  defaultCptCode?: string;
  defaultSpecimenType?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

const defaultForm: Partial<ICDCode> = { 
  icdCode: '', 
  shortDescription: '', 
  longDescription: '', 
  codeType: '', 
  category: '', 
  active: true,
  billable: false
};

const ICDCodes: React.FC = () => {
  const [codes, setCodes] = useState<ICDCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<ICDCode | null>(null);
  const [form, setForm] = useState<Partial<ICDCode>>(defaultForm);
  const [search, setSearch] = useState('');

  const fetchCodes = useCallback(async (pageNum = 0, pageSize = 20, searchTerm = '') => {
    setLoading(true);
    try {
      const data = await apiService.getIcdCodes(pageNum + 1, pageSize, searchTerm);
      setCodes(data.icdCodes || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching ICD codes:', error);
      setCodes([]);
      setTotal(0);
    }
    setLoading(false);
  }, []);

  // Debounced search effect
  useEffect(() => { 
    const timeoutId = setTimeout(() => {
      fetchCodes(page, rowsPerPage, search);
    }, 500); // 500ms delay for search debouncing

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, search, fetchCodes]);

  const handleOpenDialog = (code?: ICDCode) => {
    setEditing(code || null);
    setForm(code ? { ...code } : defaultForm);
    setOpenDialog(true);
  };
  const handleCloseDialog = () => { setOpenDialog(false); setEditing(null); setForm(defaultForm); };
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleSave = async () => {
    try {
      const payload = { ...form };
      if (editing) {
        await apiService.updateIcdCode(editing.icdCode, payload);
      } else {
        await apiService.createIcdCode(payload);
      }
      handleCloseDialog();
      fetchCodes(page, rowsPerPage, search);
    } catch (error) {
      console.error('Error saving ICD code:', error);
    }
  };
  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this ICD code?')) return;
    try {
      await apiService.deleteIcdCode(id);
      fetchCodes(page, rowsPerPage, search);
    } catch (error) {
      console.error('Error deleting ICD code:', error);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearch('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            ICD-10 Codes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage International Classification of Diseases codes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add ICD Code
        </Button>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by code, description, or category..."
            value={search}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* ICD Codes Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Code
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Short Description
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Loading ICD codes...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please wait while we fetch the data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : codes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <CodeIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {search ? 'No ICD codes found' : 'No ICD codes available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {search 
                          ? 'Try adjusting your search terms or clear the search to see all codes.'
                          : 'Get started by adding your first ICD code.'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                codes.map((code) => (
                  <TableRow 
                    key={code.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={code.icdCode} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DescriptionIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {code.shortDescription}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CategoryIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {code.category}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {code.active ? (
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        ) : (
                          <CancelIcon sx={{ fontSize: 16, color: '#f44336' }} />
                        )}
                        <Typography variant="body2" sx={{ 
                          color: code.active ? '#4caf50' : '#f44336',
                          fontWeight: 500
                        }}>
                          {code.active ? 'Active' : 'Inactive'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => handleOpenDialog(code)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(code.id)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#666'
              }
            }}
          />
        </Box>
      </Card>

      {/* ICD Code Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editing ? 'Edit ICD Code' : 'Add New ICD Code'}
        </DialogTitle>
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ICD Code"
                  name="icdCode"
                  value={form.icdCode}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code Type"
                  name="codeType"
                  value={form.codeType}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Short Description"
                  name="shortDescription"
                  value={form.shortDescription}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Long Description"
                  name="longDescription"
                  value={form.longDescription}
                  onChange={handleFormChange}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SNOMED Mapping"
                  name="snomedMapping"
                  value={form.snomedMapping}
                  onChange={handleFormChange}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Default CPT Code"
                  name="defaultCptCode"
                  value={form.defaultCptCode}
                  onChange={handleFormChange}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Default Specimen Type"
                  name="defaultSpecimenType"
                  value={form.defaultSpecimenType}
                  onChange={handleFormChange}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" gap={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={form.active}
                        onChange={handleFormChange}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="billable"
                        checked={form.billable}
                        onChange={handleFormChange}
                        color="primary"
                      />
                    }
                    label="Billable"
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button onClick={handleCloseDialog} sx={{ color: '#666' }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};
export default ICDCodes; 